import cloneDeep from "lodash.clonedeep";
import {
  getCalculationsRequestConfig,
  jsonToGeojson,
  debounce,
  parseObjectFields
} from "@/utils";
import { layerStyles, baseLayersStyles } from "../config";

export class LayerController {
  constructor(parent) {
    this.parent = parent;
    this.$store = parent.$store;
    this.mapgl = parent.mapgl;
    this.handlers = {};
    this.asyncHandlers = {};
  }

  async toggleLayerOn(id, byUser) {
    const {
      model,
      requestedFields,
      stylesConfigs
    } = this.$store.state.calculations;
    const { datatype } = model.children.find(l => l.id === id);

    const loadModelLayer = async e => {
      if (e && e.noRequest) return;
      const zoom = this.mapgl.getZoom();
      const routeName = this.parent.$route.name;
      const config = getCalculationsRequestConfig(this.parent, id, datatype);

      if (routeName !== "c-scenario") {
        config.only = ["id", "geom"];
      }

      try {
        this.$store.commit("ADD_CALC_LAYER_BY_TYPE", { type: "loading", id });
        const url = `objectInfo/${id}?config=${JSON.stringify(
          config
        )}&zoom=${zoom}`;
        const { data } = await this.$store.dispatch("REQUEST", {
          url
        });
        const geojson = jsonToGeojson(Object.keys(data).map(id => data[id]));

        if (!this.mapgl.getLayer(id)) {
          const styles =
            routeName === "c-scenario"
              ? stylesConfigs[id].style
              : baseLayersStyles[datatype];
          const options = {
            id,
            source: {
              type: "geojson",
              data: geojson
            },
            ...styles
          };

          if (this.mapgl.getLayer("gl-draw-polygon-fill-inactive.cold")) {
            this.mapgl.addLayer(options, "gl-draw-polygon-fill-inactive.cold");
          } else {
            this.mapgl.addLayer(options);
          }
        } else {
          this.mapgl.getSource(id).setData(geojson);
        }

        if (routeName === "c-scenario") {
          this.addAdditionalLayers(id, datatype);
        }

        this.$store.commit("ADD_CALC_LAYER_BY_TYPE", { type: "active", id });
        this.$store.commit("REMOVE_CALC_LAYER_BY_TYPE", {
          type: "loading",
          id
        });
      } catch (error) {
        console.log(error);
        this.$store.commit("REMOVE_CALC_LAYER_BY_TYPE", {
          type: "loading",
          id
        });
      }
    };

    // set initial configs
    const fields = await this.getObjectFields(id, datatype);
    this.$store.commit("SET_OBJECT_FIELDS", { id, fields });

    if (!requestedFields[id]) {
      const fields = ["id", "geom"];

      this.$store.commit("SET_REQUESTED_FIELDS", { id, fields });
    }

    if (!stylesConfigs[id]) {
      const config = cloneDeep(layerStyles[datatype]);

      this.$store.commit("SET_STYLE_CONFIG", { id, config });
    }

    this.handlers[id] = debounce(loadModelLayer, 200);
    this.asyncHandlers[id] = loadModelLayer;
    if (byUser) this.handlers[id]();
    this.mapgl.on("moveend", this.handlers[id]);
  }

  toggleLayerOff(id) {
    if (this.mapgl.getLayer(id)) {
      this.mapgl.removeLayer(id);
    }
    if (this.mapgl.getLayer(`${id}_border`)) {
      this.mapgl.removeLayer(`${id}_border`);
    }
    if (this.mapgl.getLayer(`${id}_diagram`)) {
      this.mapgl.removeLayer(`${id}_diagram`);
    }
    if (this.mapgl.getLayer(`${id}_diagram_border`)) {
      this.mapgl.removeLayer(`${id}_diagram_border`);
    }
    if (this.mapgl.getSource(id)) {
      this.mapgl.removeSource(id);
    }
    if (this.mapgl.getSource(`${id}_diagram`)) {
      this.mapgl.removeSource(`${id}_diagram`);
    }
    this.mapgl.off("moveend", this.handlers[id]);
    delete this.handlers[id];
    this.$store.commit("REMOVE_CALC_LAYER_BY_TYPE", { type: "active", id });

    if (this.$store.state.calculations.settingsId === id) {
      this.$store.commit("SET_CALC_FIELD", {
        field: "settingsId",
        value: null
      });
    }
  }

  // additional layers
  addAdditionalLayers(id, datatype) {
    const { stylesConfigs } = this.$store.state.calculations;

    switch (datatype) {
      case "zones": {
        if (!this.mapgl.getLayer(`${id}_border`)) {
          this.mapgl.addLayer({
            source: id,
            id: `${id}_border`,
            type: "line",
            ...stylesConfigs[id].borderStyle
          });
        }
        return;
      }
      case "links": {
        const { diagram } = stylesConfigs[id];

        if (diagram.enabled) {
          this.parent.controllers.diagram.addDiagramLayer(id);
        }
      }
    }
  }

  // update layers
  updateLayers() {
    const { activeLayers } = this.$store.state.calculations;

    activeLayers.forEach(id => this.handlers[id]());
  }

  // set only base layers
  setBaseLayers() {
    const { activeLayers, model } = this.$store.state.calculations;
    const { ids } = model;
    const baseLayersIds = ["nodes", "links"].map(type => ids[type]);
    const layers = [...activeLayers];

    layers.forEach(async id => {
      if (baseLayersIds.includes(id)) {
        this.toggleLayerOff(id);
        await this.toggleLayerOn(id);

        if (this.handlers[id]) this.handlers[id]();
      } else {
        this.toggleLayerOff(id);
      }
    });
  }

  // object fields request
  async getObjectFields(id, datatype) {
    const availableTypes = ["decimal", "float", "integer"];

    try {
      const url = `objectFields/${id}`;
      const data = await this.$store.dispatch("REQUEST", { url });
      const parsedFields = parseObjectFields(data, false);
      const fields = parsedFields.filter(f => availableTypes.includes(f.type));

      if (datatype === "links") {
        const { ids } = this.$store.state.calculations.model;
        const calcUrl = `objectFields/${ids.link_calculation_results}`;
        const calcFieldsData = await this.$store.dispatch("REQUEST", {
          url: calcUrl
        });
        const parsedCalcFields = parseObjectFields(calcFieldsData, false);
        const calcFields = parsedCalcFields
          .filter(f => availableTypes.includes(f.type))
          .map(f => ({
            ...f,
            calc_field: true
          }));

        return [...fields, ...calcFields];
      }

      return fields;
    } catch (error) {
      console.log(error);

      return [];
    }
  }

  async updateFieldData(id, field) {
    const {
      objectFields,
      requestedFields,
      currentScenarioId
    } = this.$store.state.calculations;

    // if (requestedFields[id].includes(field)) return;

    let isCalcField = false;

    const fields = objectFields[id];
    const only = [field].map(name => {
      const fieldConfig = fields.find(field => field.title === name);

      if (fieldConfig) {
        const { calc_field } = fieldConfig;

        if (calc_field) {
          isCalcField = true;

          return `link_calculation_results.${name}`;
        }

        return name;
      }

      return name;
    });

    const config = { only };

    if (isCalcField) {
      config.where = [
        {
          field: "link_calculation_results.calculation_id",
          value: currentScenarioId,
          op: "="
        }
      ];
    }

    const url = `aggregation/${id}?config=${JSON.stringify(config)}`;
    const { data } = await this.$store.dispatch("REQUEST", { url });

    const fieldName = isCalcField ? `link_calculation_results_${field}` : field;

    const fieldMeta = data[fieldName];
    const index = fields.findIndex(f => f.title === field);
    const newValue = {
      ...fields[index],
      ...fieldMeta
    };

    fields[index] = newValue;

    this.$store.commit("SET_REQUESTED_FIELDS", {
      id,
      fields: [...requestedFields[id], field]
    });

    await this.asyncHandlers[id]();
  }
}
